import {
	ActionIcon,
	Box,
	createStyles,
	Divider,
	Group,
	Modal,
	Stack,
	useMantineTheme,
} from '@mantine/core';
import type {
	SecodaAIProposalOut,
	SuggestDescriptionArg,
	SuggestGlossaryArg,
	SuggestMonitorArg,
} from '@repo/api-codegen';
import { Button, Icon, Text } from '@repo/foundations';
import { type MouseEvent, useMemo, useState } from 'react';
import { DescriptionSuggestionTable } from './DescriptionSuggestionTable';
import type { DocumentCreationProposalData } from './DocumentSuggestionRenderer';
import { DocumentSuggestionTable } from './DocumentSuggestionTable';
import { GlossarySuggestionTable } from './GlossarySuggestionTable';
import { MonitorSuggestionTable } from './MonitorSuggestionTable';
import { ProposalActions } from './ProposalActions';
import { ProposalRenderer } from './ProposalRenderer';

const useStyles = createStyles(
	(
		theme,
		{
			boundedSelectedProposalIndex,
		}: { boundedSelectedProposalIndex: number | null }
	) => ({
		modalBody: {
			padding: 0,
			paddingTop: '0 !important',
		},
		inlineContainer: {
			border: `1px solid ${theme.other.getColor('border/ai/default')}`,
			borderRadius: theme.radius.md,
			backgroundColor: theme.other.getColor('surface/ai/default'),
		},
		inlineHeader: {
			borderTopLeftRadius: theme.radius.md,
			borderTopRightRadius: theme.radius.md,
		},
		inlineContent: {
			borderRadius: theme.radius.md,
			borderTop: `1px solid ${theme.other.getColor('border/ai/default')}`,
			backgroundColor: theme.other.getColor('surface/primary/default'),
		},
		backButton: {
			cursor: 'pointer',
		},
		scrollAreaViewport: {
			height: boundedSelectedProposalIndex !== null ? 400 : undefined,
			overflowY: 'auto',
		},
	})
);

export interface ProposalsListModalProps {
	data: SecodaAIProposalOut[];
	mode:
		| {
				type: 'inline_list';
				noReviewButton?: boolean;
		  }
		| {
				type: 'modal_list';
				triggerRenderer: (open: () => void, isOpen: boolean) => React.ReactNode;
		  };
}

export function ProposalsListModal({ data, mode }: ProposalsListModalProps) {
	const [isOpen, setIsOpen] = useState(false);

	const theme = useMantineTheme();

	const [selectedProposalIndex, setSelectedProposalIndex] = useState<
		number | null
	>(null);

	const boundedSelectedProposalIndex = useMemo(() => {
		if (selectedProposalIndex === null) {
			return null;
		}

		return Math.max(0, Math.min(selectedProposalIndex, data.length - 1));
	}, [selectedProposalIndex, data]);

	const { classes } = useStyles({ boundedSelectedProposalIndex });

	const nextPendingProposalIndex = useMemo(() => {
		if (boundedSelectedProposalIndex === null) {
			return null;
		}

		// eslint-disable-next-line no-plusplus
		for (let i = boundedSelectedProposalIndex + 1; i < data.length; i++) {
			if (data[i].status === 'pending') {
				return i;
			}
		}

		// eslint-disable-next-line no-plusplus
		for (let i = 0; i < boundedSelectedProposalIndex; i++) {
			if (data[i].status === 'pending') {
				return i;
			}
		}

		return (boundedSelectedProposalIndex + 1) % data.length;
	}, [boundedSelectedProposalIndex, data]);

	const open = () => {
		setIsOpen(true);
		setSelectedProposalIndex(null);
	};

	const close = () => setIsOpen(false);

	const onRowClick = (index: number, event: MouseEvent) => {
		setSelectedProposalIndex(index);
		setIsOpen(true);
		event.stopPropagation();
	};

	const onReviewClick = (event: MouseEvent) => {
		onRowClick(0, event);
	};

	const suggestionsTitle = useMemo(() => {
		if (data.length === 0) {
			return '';
		}

		if (data[0].data.proposal_type === 'glossary_term_creation') {
			return 'Suggested glossary terms';
		}

		if (data[0].data.proposal_type === 'monitor_creation') {
			return 'Suggested monitors';
		}

		if (data[0].data.proposal_type === 'description_suggestion') {
			return 'Suggested descriptions';
		}

		if (data[0].data.proposal_type === 'document_creation') {
			return 'Suggested documents';
		}

		return '';
	}, [data]);

	const listContent = () => {
		if (!data || data.length === 0) {
			return null;
		}

		if (data[0].data.proposal_type === 'glossary_term_creation') {
			return (
				<GlossarySuggestionTable
					onRowClick={(row, index, event) => {
						onRowClick(index, event);
					}}
					suggestions={data.map((proposal) => ({
						proposal,
						data: proposal.data.data as SuggestGlossaryArg,
					}))}
				/>
			);
		}

		if (data[0].data.proposal_type === 'monitor_creation') {
			return (
				<MonitorSuggestionTable
					onRowClick={(row, index, event) => {
						onRowClick(index, event);
					}}
					suggestions={data.map((proposal) => ({
						proposal,
						data: proposal.data.data as SuggestMonitorArg,
					}))}
				/>
			);
		}

		if (data[0].data.proposal_type === 'description_suggestion') {
			return (
				<DescriptionSuggestionTable
					onRowClick={(row, index, event) => {
						onRowClick(index, event);
					}}
					suggestions={data.map((proposal) => ({
						proposal,
						data: proposal.data.data as SuggestDescriptionArg,
					}))}
				/>
			);
		}

		if (data[0].data.proposal_type === 'document_creation') {
			return (
				<DocumentSuggestionTable
					onRowClick={(row, index, event) => {
						onRowClick(index, event);
					}}
					suggestions={data.map((proposal) => ({
						proposal,
						data: proposal.data.data as DocumentCreationProposalData,
					}))}
				/>
			);
		}

		return null;
	};

	const modalContent = (): React.ReactNode => {
		if (boundedSelectedProposalIndex !== null) {
			return (
				<Stack spacing={0}>
					<ProposalRenderer proposal={data[boundedSelectedProposalIndex]} />
				</Stack>
			);
		}

		const listView = listContent();

		return listView;
	};

	const goPrevious = () => {
		setSelectedProposalIndex(
			boundedSelectedProposalIndex !== null && boundedSelectedProposalIndex > 0
				? boundedSelectedProposalIndex - 1
				: boundedSelectedProposalIndex
		);
	};

	const goNext = () => {
		setSelectedProposalIndex(
			boundedSelectedProposalIndex !== null &&
				boundedSelectedProposalIndex < data.length - 1
				? boundedSelectedProposalIndex + 1
				: boundedSelectedProposalIndex
		);
	};

	const goNextPending = () => {
		setSelectedProposalIndex(nextPendingProposalIndex);
	};

	const actionsRenderer = () => {
		if (boundedSelectedProposalIndex === null || !data) {
			return null;
		}
		const total = data.length;
		const current = boundedSelectedProposalIndex + 1;

		return (
			<Group position="apart" align="center" p="md">
				{/* Left: Pagination controls */}
				<Group spacing="xs" align="center">
					{/* Chevrons next to each other */}
					<Group spacing={0}>
						<ActionIcon
							size="sm"
							variant="subtle"
							onClick={goPrevious}
							disabled={boundedSelectedProposalIndex === 0}
							aria-label="Previous suggestion"
						>
							<Icon
								name="chevronLeft"
								size="md"
								color={
									boundedSelectedProposalIndex === 0
										? 'icon/primary/disabled'
										: 'icon/primary/default'
								}
							/>
						</ActionIcon>
						<ActionIcon
							size="sm"
							variant="subtle"
							onClick={goNext}
							disabled={boundedSelectedProposalIndex === total - 1}
							aria-label="Next suggestion"
						>
							<Icon
								name="chevronRight"
								size="md"
								color={
									boundedSelectedProposalIndex === total - 1
										? 'icon/primary/disabled'
										: 'icon/primary/default'
								}
							/>
						</ActionIcon>
					</Group>
					<Text size="sm">
						{current} of {total} suggestions
					</Text>
				</Group>
				{/* Right: Action buttons */}
				<Group spacing={8}>
					<ProposalActions
						proposal={data[boundedSelectedProposalIndex]}
						postApprovalAction={goNextPending}
					/>
				</Group>
			</Group>
		);
	};

	return (
		<>
			{mode.type === 'modal_list' && mode.triggerRenderer(open, isOpen)}
			{mode.type === 'inline_list' &&
				(mode.noReviewButton ? (
					listContent()
				) : (
					<Stack className={classes.inlineContainer} spacing={0}>
						<Group
							className={classes.inlineHeader}
							p="sm"
							pl="lg"
							position="apart"
						>
							<Group>
								<Icon name="sparkles" size="md" color="icon/ai/default" />
								<Text weight="semibold" color="text/ai/default" size="sm">
									{suggestionsTitle}
								</Text>
							</Group>
							<Button onClick={onReviewClick}>Review</Button>
						</Group>
						<Box className={classes.inlineContent}>{listContent()}</Box>
					</Stack>
				))}
			<Modal
				opened={isOpen}
				onClose={close}
				title={
					boundedSelectedProposalIndex !== null ? (
						<Group
							spacing={4}
							align="center"
							className={classes.backButton}
							onClick={() => setSelectedProposalIndex(null)}
						>
							<ActionIcon
								variant="subtle"
								size="md"
								aria-label="Back to suggestions"
							>
								<Icon name="chevronLeft" size="md" />
							</ActionIcon>
							<span>Back to suggestions</span>
						</Group>
					) : (
						'Suggestions'
					)
				}
				size="xl"
				classNames={{ body: classes.modalBody }}
			>
				<Stack spacing={0} p={0}>
					<Box className={classes.scrollAreaViewport}>{modalContent()}</Box>
					{boundedSelectedProposalIndex !== null && (
						<>
							<Divider />
							{actionsRenderer()}
						</>
					)}
				</Stack>
			</Modal>
		</>
	);
}
